import React, { useMemo, useState, useEffect } from "react";

import classnames from "classnames";

import CustomButton, { CustomButtonProps } from "../custom-button/CustomButton";

import useInterval from "../../../hooks/useInterval";

import ColorSchema from "../../../assets/kits/colors";

interface SnackBarProps {
  className?: string;
  style?: React.CSSProperties;

  // text styles
  text: string;
  pTagProps?: React.HTMLAttributes<HTMLParagraphElement>;

  // button styles
  button?: boolean;
  buttonContent?: string | JSX.Element | JSX.Element[];
  buttonProps?: CustomButtonProps;

  //colors
  backgroundColor?: keyof typeof ColorSchema;
  color?: keyof typeof ColorSchema;
  borderColor?: keyof typeof ColorSchema;
}

const SnackBar: React.FC<SnackBarProps> = ({
  className,
  style,
  text,
  pTagProps,
  button,
  buttonContent,
  buttonProps: buttonPropsMain,
  borderColor = ColorSchema.foreground_outline_darker,
  backgroundColor = ColorSchema.schema_surface_light,
  color = ColorSchema.foreground_surface_me,
}) => {
  const {
    style: buttonStyle = {},
    className: buttonClassName = "",
    buttonStyle: buttonType = "text",
    ...buttonProps
  } = buttonPropsMain || {};
  const { className: pTagClassName } = pTagProps || { className: "" };

  const containerStyles = useMemo<React.CSSProperties>(
    () => ({
      border: "1px solid transparent",
      borderColor: borderColor,
      backgroundColor: backgroundColor,
      color,
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      ...style,
    }),
    [style, borderColor, backgroundColor, color]
  );

  return (
    <div
      className={classnames(
        {
          // check wether a padding class is sended for y axis or not.
          "pd-y-14": !(
            className?.includes("pd-y") ||
            className?.includes("pd-t") ||
            className?.includes("pd-b")
          ),
          // check wether a padding class is sended for x axis or not.
          "pd-x-16": !(
            className?.includes("pd-x") ||
            className?.includes("pd-r") ||
            className?.includes("pd-l")
          ),
        },
        className
      )}
      style={containerStyles}
    >
      <p
        {...{
          className: classnames(
            {
              // check wether a font class is sended or not.
              "txt-t2-r": !(
                pTagProps?.className?.includes("txt-") ||
                pTagProps?.className?.includes("title-") ||
                pTagProps?.className?.includes("desc-")
              ),
            },
            pTagClassName
          ),
          ...pTagProps,
        }}
      >
        {text}
      </p>
      {button && (
        <CustomButton
          {...{
            className: classnames(
              {
                // check wether a padding class is sended for y axis or not.
                "pd-y-4": !(className && /pd-([ytb]*-)?\d/.test(className)),
                // check wether a padding class is sended for x axis or not.
                "pd-x-12": !(className && /pd-([xrl]*-)?\d/.test(className)),
                // check wether a margin class is sended for right side or not.
                "mr-r-8": !(className && /mr-([xr]*-)?\d/.test(className)),
              },
              buttonClassName
            ),
            buttonStyle: buttonType,
            style: {
              flexShrink: 0,
              ...buttonStyle,
            },
            ...buttonProps,
          }}
        >
          {buttonContent}
        </CustomButton>
      )}
    </div>
  );
};

export default SnackBar;

export type { SnackBarProps };
